import React, { useState, useEffect, useCallback } from "react";
import { Manager } from "@components/form/state/types";
import { LandingZoneModel } from "@api";
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { useApproverDistroSelect, useBusinessTypeSelect } from "@hooks";
import { Form } from "@components";
import { Field } from "@components/form/fields/field";
import { postLandingZone, putLandingZone } from "@api/admin/landingzones";
import { alertService } from "@services";
import { useLandingZoneStore } from "../../../../../../store/hooksy/landingZones";

type Key = keyof LandingZoneModel;

type Props = {
  open: boolean;
  onCancel: () => void;
  form: Manager<LandingZoneModel>;
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

export const LandingZoneDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  // custom hooks for distros
  const {
    approverDistros,
    isFetchingApproverDistros,
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
    setSelectedApproverDistro2,
    setSelectedApproverDistro3,
    setSelectedApproverDistro4,
    handleApproverDistroTypeChange,
  } = useApproverDistroSelect(
    null,
    form.values.approverDistro2Id,
    form.values.approverDistro3Id,
    form.values.approverDistro4Id
  );

  // custom hooks for system types/business units
  const {
    selectedBusinessType,
    selectedBusinessTypeName,
    businessTypes,
    isFetchingbusinessTypes,
    setSelectedBusinessType,
    setSelectedBusinessTypeName,
    handleBusinessTypeChange,
  } = useBusinessTypeSelect(0, form.values.businessUnitName);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [, setLandingZoneHooksyStore] = useLandingZoneStore();

  useEffect(() => {
    if (selectedApproverDistro2) {
      form.set(Form.target.VALUE, ["approver2", ""]);
    }
    if (selectedApproverDistro3) {
      form.set(Form.target.VALUE, ["approver3", ""]);
    }
    if (selectedApproverDistro4) {
      form.set(Form.target.VALUE, ["approver4", ""]);
    }
  }, [
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
  ]);

  const [businessTypeName, setBusinessTypeName] = useState("");
  useEffect(() => {
    if (selectedBusinessType) {
      const businessTypeFilteredById: any = businessTypes.find(
        (x: any) => x.value === selectedBusinessType
      );
      setBusinessTypeName(businessTypeFilteredById?.label ?? "");
    } else {
      setBusinessTypeName(
        form.values.businessUnitName ? form.values.businessUnitName : ""
      );
    }
  }, [selectedBusinessType, businessTypes, form]);

  // function getBusinessTypeName(targetId: number): string {
  //   const businessType: any = businessTypes.find(
  //     (bUnit: any) => bUnit.id === targetId
  //   );
  //   return businessType.value;
  // }

  let landingZoneModel: LandingZoneModel = {
    id: 0,
    name: "",
    isActive: false,
    approver2: "",
    approver3: "",
    approver4: "",
    approverDistro2Id: 0,
    approverDistro2Name: "",
    approverDistro3Id: 0,
    approverDistro3Name: "",
    approverDistro4Id: 0,
    approverDistro4Name: "",
    businessUnitId: 0,
    businessUnitName: "",
    assignmentTeam: "",
    accessDetailNote: "",
    uamNote: "",
    metadata: "",
  };

  function clearModelAndClosePopup() {
    landingZoneModel.id = 0;
    landingZoneModel.name = "";
    landingZoneModel.isActive = false;
    landingZoneModel.approver2 = "";
    landingZoneModel.approver3 = "";
    landingZoneModel.approver4 = "";
    landingZoneModel.approverDistro2Id = 0;
    landingZoneModel.approverDistro2Name = "";
    landingZoneModel.approverDistro3Id = 0;
    landingZoneModel.approverDistro3Name = "";
    landingZoneModel.approverDistro4Id = 0;
    landingZoneModel.approverDistro4Name = "";
    landingZoneModel.businessUnitId = 0;
    landingZoneModel.businessUnitName = "";
    landingZoneModel.assignmentTeam = "";
    landingZoneModel.accessDetailNote = "";
    landingZoneModel.uamNote = "";
    landingZoneModel.metadata = "";
    form.reset();
    onCancel();
  }

  function onChange(name: Key, value: any) {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
    if (name === "approver2") setSelectedApproverDistro2(0);
    if (name === "approver3") setSelectedApproverDistro3(0);
    if (name === "approver4") setSelectedApproverDistro4(0);
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "name":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitPayload();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [
    form,
    selectedBusinessTypeName,
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
    businessTypeName,
  ]);

  function submitPayload() {
    landingZoneModel.id = form.values.id;
    landingZoneModel.name = form.values.name;
    landingZoneModel.approver2 = form.values.approver2
      ? form.values.approver2
      : "";
    landingZoneModel.approver3 = form.values.approver3
      ? form.values.approver3
      : "";
    landingZoneModel.approver4 = form.values.approver4
      ? form.values.approver4
      : "";
    landingZoneModel.approverDistro2Id = selectedApproverDistro2
      ? selectedApproverDistro2
      : 0;
    landingZoneModel.approverDistro3Id = selectedApproverDistro3
      ? selectedApproverDistro3
      : 0;
    landingZoneModel.approverDistro4Id = selectedApproverDistro4
      ? selectedApproverDistro4
      : 0;
    landingZoneModel.businessUnitId = selectedBusinessType
      ? selectedBusinessType
      : 0;
    landingZoneModel.assignmentTeam = form.values.assignmentTeam
      ? form.values.assignmentTeam
      : "";
    landingZoneModel.accessDetailNote = form.values.accessDetailNote
      ? form.values.accessDetailNote
      : "";
    landingZoneModel.uamNote = form.values.uamNote ? form.values.uamNote : "";
    landingZoneModel.metadata = form.values.metadata
      ? form.values.metadata
      : "";
    landingZoneModel.isActive = form.values.isActive;
    // console.log(landingZoneModel);
    setIsSubmitting(true);
    // update
    if (landingZoneModel.id > 0) {
      putLandingZone(landingZoneModel)
        .then((response) =>
          alertService.success({ msg: "Landing Zone sucessfully updated!" })
        )
        .catch((error) =>
          alertService.danger({ statusCode: error.response.status })
        )
        .finally(() => {
          setIsSubmitting(false);
          clearModelAndClosePopup();
          setLandingZoneHooksyStore({ fetchList: true });
        });
    }
    // create
    else {
      postLandingZone(landingZoneModel)
        .then((response) =>
          alertService.success({ msg: "Landing Zone sucessfully created!" })
        )
        .catch((error) =>
          alertService.danger({ statusCode: error.response.status })
        )
        .finally(() => {
          setIsSubmitting(false);
          clearModelAndClosePopup();
          setLandingZoneHooksyStore({ fetchList: true });
        });
    }
  }

  const modalTitle = form.values.id === 0 ? "Add New" : "Update";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  let approverDistroMenu = null;
  if (isFetchingApproverDistros) {
    approverDistroMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    approverDistroMenu = approverDistros.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  let businessTypeMenu = null;
  if (isFetchingbusinessTypes) {
    businessTypeMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    businessTypeMenu = businessTypes.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  return (
    <Box mt={2}>
      <Dialog open={open} fullWidth>
        <DialogTitle
          id="form-dialog-title"
          onClose={() => clearModelAndClosePopup()}
        >
          {`${modalTitle} Landing Zone`}
        </DialogTitle>
        <DialogContent>
          <Form>
            <Form.Textbox
              label="Landing Zone Name"
              name="name"
              onChange={onChange}
              size={12}
              error={form.errors.name}
              value={form.values.name}
              required
            />
            <Form.Textbox
              label="Approver 2"
              name="approver2"
              onChange={onChange}
              size={12}
              error={form.errors.approver2}
              value={form.values.approver2}
            />
            <Form.Textbox
              label="Approver 3"
              name="approver3"
              onChange={onChange}
              size={12}
              error={form.errors.approver3}
              value={form.values.approver3}
            />
            <Form.Textbox
              label="Approver 4"
              name="approver4"
              onChange={onChange}
              size={12}
              error={form.errors.approver4}
              value={form.values.approver4}
            />
            <Field label="Approver Distro 2" size={12}>
              <Select
                name="approverDistro2"
                value={selectedApproverDistro2}
                onChange={handleApproverDistroTypeChange}
                disabled={isFetchingApproverDistros}
              >
                {approverDistroMenu}
              </Select>
            </Field>
            <Field label="Approver Distro 3" size={12}>
              <Select
                name="approverDistro3"
                value={selectedApproverDistro3}
                onChange={handleApproverDistroTypeChange}
                disabled={isFetchingApproverDistros}
              >
                {approverDistroMenu}
              </Select>
            </Field>
            <Field label="Approver Distro 4" size={12}>
              <Select
                name="approverDistro4"
                value={selectedApproverDistro4}
                onChange={handleApproverDistroTypeChange}
                disabled={isFetchingApproverDistros}
              >
                {approverDistroMenu}
              </Select>
            </Field>
            <Field label="Business Unit" size={12}>
              <Select
                name="businessUnit"
                value={selectedBusinessType}
                onChange={handleBusinessTypeChange}
                disabled={isFetchingbusinessTypes}
              >
                {businessTypeMenu}
              </Select>
            </Field>
            <Form.Textbox
              label="Meta Data"
              name="metadata"
              onChange={onChange}
              size={12}
              error={form.errors.metadata}
              value={form.values.metadata}
            />
            <Form.Textbox
              label="UAM Note"
              name="uamNote"
              onChange={onChange}
              size={12}
              error={form.errors.uamNote}
              value={form.values.uamNote}
            />
            <Form.Textbox
              label="Access Detail Note"
              name="accessDetailNote"
              onChange={onChange}
              size={12}
              error={form.errors.accessDetailNote}
              value={form.values.accessDetailNote}
            />
            <Form.Textbox
              label="Service Manager Queue"
              name="assignmentTeam"
              onChange={onChange}
              size={6}
              error={form.errors.assignmentTeam}
              value={form.values.assignmentTeam}
            />
            <Form.Checkbox
              label="Is Active"
              name="isActive"
              onChange={onChange}
              size={12}
              value={form.values.isActive}
            />
          </Form>
          <Box textAlign="right" mt={4}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => clearModelAndClosePopup()}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={handleSubmit}
              style={{ marginLeft: "40px" }}
              disabled={
                isSubmitting ||
                isFetchingApproverDistros ||
                isFetchingbusinessTypes
              }
            >
              {submitText}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
